import { Info, Lock, LogOut, Sparkles, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";

import { SlidePreview } from "@/components/slide-preview/SlidePreview";
import { Button } from "@/components/ui/button";
import { JsonViewer } from "@/components/viewer/JsonViewer";
import { AiDrawer, type AiEditResult } from "@/features/ai/AiDrawer";
import { ChangeSummary } from "@/features/ai/ChangeSummary";
import type { FlatBundle, Slide } from "@/lib/types";
import { cn } from "@/lib/utils";
import { useAuthStore } from "@/stores/authStore";
import { useBundleStore } from "@/stores/bundleStore";

export function AiGatePage() {
  const { authed, checking, error, login, logout, recheck } = useAuthStore();
  const [password, setPassword] = useState("");

  useEffect(() => {
    recheck();
  }, [recheck]);

  if (authed) {
    return <AiPanel onLogout={logout} />;
  }

  return (
    <section className="mx-auto max-w-md px-4 py-12 sm:px-6 sm:py-20">
      <div className="rounded-[14px] border border-[var(--color-border)] bg-[var(--color-surface)] p-6 sm:p-8">
        <div className="mb-6 flex items-center gap-3">
          <Lock size={20} className="text-[var(--color-coral)]" />
          <h1 className="font-display text-2xl font-medium">AI 編集 (β)</h1>
        </div>
        <p className="mb-6 text-sm text-[var(--color-ink-muted)]">
          AI 編集機能はパスワードで保護されています。パスワードをお持ちでない方は、
          アプリ画面でスライド JSON をコピーして ChatGPT / Gemini に直接貼り付けて
          編集後、復元ページから .pptx に戻す方法でお試しいただけます。
        </p>
        <form
          onSubmit={async (e) => {
            e.preventDefault();
            await login(password);
          }}
          className="flex flex-col gap-3"
        >
          <input
            type="password"
            placeholder="パスワード"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            className="h-11 rounded-[10px] border border-[var(--color-border)] bg-[var(--color-surface)] px-3 text-base focus:border-[var(--color-coral)] focus:outline-none sm:h-10 sm:text-sm"
          />
          <Button type="submit" disabled={checking || !password}>
            {checking ? "認証中…" : "ログイン"}
          </Button>
          {error && <p className="text-sm text-[var(--color-coral-dark)]">{error}</p>}
        </form>
      </div>
    </section>
  );
}

function ExplainBanner() {
  return (
    <div className="mb-4 flex gap-3 rounded-[10px] border border-[var(--color-border)] bg-[var(--color-surface-muted)] p-3 text-xs leading-relaxed text-[var(--color-ink-muted)] sm:p-4">
      <Info size={14} className="mt-0.5 shrink-0 text-[var(--color-coral)]" />
      <div>
        このページでは、選んだスライドの
        <code className="mx-1 rounded px-1 font-mono text-[var(--color-ink)]">JSON</code>
        と指示文を Vertex AI Gemini に送り、 返ってきた修正版 JSON でスライドを更新します。 実際に
        PowerPoint を編集する代わりに、中間フォーマットの JSON を AI に触らせる
        ことで、何が変わったかを差分として確認できます。
        編集が気に入ったら「適用」で反映、その後アプリ画面で
        <code className="mx-1 rounded px-1 font-mono text-[var(--color-ink)]">.pptx</code>
        に戻せます。
      </div>
    </div>
  );
}

function AiPanel({ onLogout }: { onLogout: () => Promise<void> }) {
  const { bundle, selectedSlideIndex, setSelected, setBundle, reset } = useBundleStore();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [prevSnapshot, setPrevSnapshot] = useState<Slide | null>(null);
  const [pending, setPending] = useState<AiEditResult | null>(null);
  const [viewMode, setViewMode] = useState<"preview" | "json">("preview");

  const handleReset = () => {
    const ok = window.confirm(
      "現在のファイル データをすべて削除します。編集中の内容も失われます。よろしいですか？",
    );
    if (ok) reset();
  };

  if (!bundle) {
    return (
      <section className="mx-auto max-w-3xl px-4 py-10 sm:px-6 sm:py-16">
        <div className="mb-6 flex items-center justify-between gap-2 sm:mb-8">
          <h1 className="font-display text-2xl font-medium sm:text-3xl">AI 編集 (β)</h1>
          <Button variant="secondary" size="sm" onClick={onLogout}>
            <LogOut size={14} /> ログアウト
          </Button>
        </div>
        <ExplainBanner />
        <div className="rounded-[14px] border border-[var(--color-border)] bg-[var(--color-surface)] p-6 text-center sm:p-8">
          <p className="mb-2 font-display text-lg">まずアプリで PPTX をアップロードしてください</p>
          <p className="text-sm text-[var(--color-ink-muted)]">
            <a className="text-[var(--color-coral)] hover:underline" href="/app">
              /app
            </a>
            で .pptx をドロップ → 変換後にこのページへ戻ってくると、スライド単位で AI
            編集ができます。
          </p>
        </div>
      </section>
    );
  }

  const slide = bundle.structure.find((s) => s.slide_index === selectedSlideIndex);
  if (!slide) return null;

  const applyEdit = (newSlide: Slide) => {
    setPrevSnapshot(slide);
    const updated: FlatBundle = {
      ...bundle,
      structure: bundle.structure.map((s) =>
        s.slide_index === newSlide.slide_index ? newSlide : s,
      ),
    };
    setBundle(updated);
    setPending(null);
    setDrawerOpen(false);
  };

  const undo = () => {
    if (!prevSnapshot) return;
    const restored: FlatBundle = {
      ...bundle,
      structure: bundle.structure.map((s) =>
        s.slide_index === prevSnapshot.slide_index ? prevSnapshot : s,
      ),
    };
    setBundle(restored);
    setPrevSnapshot(null);
  };

  return (
    <div className="mx-auto grid max-w-[1400px] grid-cols-1 gap-4 px-4 py-5 sm:px-6 sm:py-8 md:gap-6 lg:grid-cols-[220px_1fr]">
      <aside className="flex flex-col gap-2">
        <div className="flex items-center justify-between gap-2">
          <h2 className="font-display text-xs font-medium uppercase tracking-wide text-[var(--color-ink-muted)] sm:text-sm">
            スライド ({bundle.manifest.slides.length})
          </h2>
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleReset}
              aria-label="別のファイルをアップロードし直す"
              title="別のファイル"
            >
              <Trash2 size={14} />
            </Button>
            <Button variant="ghost" size="sm" onClick={onLogout} aria-label="ログアウト">
              <LogOut size={14} />
            </Button>
          </div>
        </div>
        <p
          className="truncate text-[11px] text-[var(--color-ink-soft)]"
          title={bundle.manifest.source.filename}
        >
          {bundle.manifest.source.filename}
        </p>

        <label className="lg:hidden" htmlFor="ai-slide-select">
          <span className="sr-only">スライドを選択</span>
          <select
            id="ai-slide-select"
            value={selectedSlideIndex ?? ""}
            onChange={(e) => {
              setSelected(Number(e.target.value));
              setPending(null);
            }}
            className="h-11 w-full rounded-[10px] border border-[var(--color-border)] bg-[var(--color-surface)] px-3 text-sm focus:border-[var(--color-coral)] focus:outline-none"
          >
            {bundle.manifest.slides.map((s) => (
              <option key={s.index} value={s.index}>
                {String(s.index).padStart(2, "0")} · {s.title}
              </option>
            ))}
          </select>
        </label>

        <div className="hidden max-h-[calc(100vh-8rem)] flex-col gap-1 overflow-auto pr-1 lg:flex">
          {bundle.manifest.slides.map((s) => (
            <button
              key={s.index}
              type="button"
              onClick={() => {
                setSelected(s.index);
                setPending(null);
              }}
              className={cn(
                "rounded-[10px] px-3 py-2 text-left text-sm transition-colors",
                s.index === selectedSlideIndex
                  ? "bg-[var(--color-coral)] text-white"
                  : "hover:bg-[var(--color-surface-muted)]",
              )}
            >
              <span
                className={cn(
                  "font-mono text-xs",
                  s.index === selectedSlideIndex ? "text-white/70" : "text-[var(--color-ink-soft)]",
                )}
              >
                {String(s.index).padStart(2, "0")}
              </span>{" "}
              {s.title}
            </button>
          ))}
        </div>
      </aside>

      <section className="min-w-0">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
          <h1 className="font-display text-xl font-medium sm:text-2xl">
            slide {selectedSlideIndex}
          </h1>
          <div className="flex flex-wrap items-center gap-2">
            <div className="inline-flex rounded-[10px] border border-[var(--color-border)] bg-[var(--color-surface)] p-1 text-sm">
              <button
                type="button"
                onClick={() => setViewMode("preview")}
                className={cn(
                  "rounded-[8px] px-3 py-1",
                  viewMode === "preview" && "bg-[var(--color-coral)] text-white",
                )}
              >
                プレビュー
              </button>
              <button
                type="button"
                onClick={() => setViewMode("json")}
                className={cn(
                  "rounded-[8px] px-3 py-1",
                  viewMode === "json" && "bg-[var(--color-coral)] text-white",
                )}
              >
                JSON
              </button>
            </div>
            {prevSnapshot && (
              <Button variant="secondary" size="sm" onClick={undo}>
                直前の編集を取り消す
              </Button>
            )}
            <Button size="sm" onClick={() => setDrawerOpen(true)}>
              <Sparkles size={14} /> AI 編集
            </Button>
          </div>
        </div>

        <ExplainBanner />

        {pending ? (
          <div className="flex flex-col gap-3">
            <p className="font-display text-sm text-[var(--color-ink-muted)]">
              AI の提案が届きました。上が編集前、下が編集後です（PC では左右に並びます）。
            </p>
            {pending.ai_summary && (
              <div className="rounded-[10px] border border-[var(--color-border)] bg-[var(--color-coral-soft)]/30 p-4 text-sm">
                <div className="text-[11px] uppercase tracking-wide text-[var(--color-ink-muted)]">
                  AI の説明
                </div>
                <div className="mt-1 font-medium text-[var(--color-ink)]">{pending.ai_summary}</div>
                <div className="mt-1 text-[11px] text-[var(--color-ink-muted)]">
                  提案 {pending.planned_count} 件 / 適用 {pending.operations_count} 件
                </div>
              </div>
            )}
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <div className="mb-2 text-xs font-medium uppercase tracking-wide text-[var(--color-ink-muted)]">
                  編集前
                </div>
                <SlidePreview slide={slide} media={bundle.assets.media} />
              </div>
              <div>
                <div className="mb-2 text-xs font-medium uppercase tracking-wide text-[var(--color-coral)]">
                  編集後
                </div>
                <SlidePreview slide={pending.new_slide} media={bundle.assets.media} />
              </div>
            </div>
            <ChangeSummary before={slide} after={pending.new_slide} />
            <div className="mt-2 flex flex-col gap-2 sm:flex-row">
              <Button onClick={() => applyEdit(pending.new_slide)}>適用</Button>
              <Button variant="secondary" onClick={() => setPending(null)}>
                破棄
              </Button>
            </div>
          </div>
        ) : viewMode === "preview" ? (
          <SlidePreview slide={slide} media={bundle.assets.media} />
        ) : (
          <JsonViewer root={slide} initialExpandedDepth={2} />
        )}
      </section>

      {drawerOpen && (
        <AiDrawer
          slide={slide}
          onApply={(result) => {
            setPending(result);
            setDrawerOpen(false);
          }}
          onClose={() => setDrawerOpen(false)}
        />
      )}
    </div>
  );
}
